import { type ReactNode, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { twMerge } from 'tailwind-merge';

import DataTable from '@/components/tables/DataTable';
import type {
  DateRange,
  LocationData,
  LocationPlaysResponse,
  ShowFilter,
  TableData,
  TableHeader,
} from '@/types';
import { Button, Loader, Switch } from '@acast-tech/decibel';
import {
  ArrowDownToLineLightGlyph,
  ChevronRightRegularGlyph,
  SearchLightGlyph,
} from '@acast-tech/decibel-glyphs';

import Modal from '@/components/modals/Modal/Modal';
import NoDataMessage from '@/components/states/NoDataMessage';
import { useDerivedNetworkContext } from '@/context';
import LocationMap from '@/routes/AudiencePage/charts/MapChart/LocationMap';
import {
  getCountryFlagEmoji,
  getCountryName,
  hasCountryFlag,
} from '@/routes/AudiencePage/charts/MapChart/countryUtils';
import { useLocationPlaysQuery } from '@/services/revenueApi';
import { Platform } from '@/types';
import { isSocialPlatform } from '@/types/api/platforms';
import useLocationCsvExport from './useLocationCsvExport';
import {
  trackAudienceLocationModalDownloadClicked,
  trackAudienceLocationModalHideSocialsToggled,
  trackAudienceLocationModalOpened,
} from './audienceModalMixpanel';

type LocationDetailsRow = {
  key: string;
  country: string;
  [platformMetric: string]: string | number | null;
};

type LocationDetailsColumn = {
  key: string;
  header: ReactNode;
  csvHeader?: string;
  align?: 'left' | 'right';
  className?: string;
  sortable?: boolean;
  type?: 'number' | 'percentage';
  render: (row: LocationDetailsRow) => ReactNode;
};

const mapLegendItems = [
  { label: '>20%', color: '#5a97e3' },
  { label: '>0-20%', color: '#a9caf1' },
  { label: 'No data', color: '#e3e4e7' },
];

const DEFAULT_PAGE_SIZE = 10;
const TOTAL_PERCENTAGE_KEY = 'totalPercentage';

const isPodcastPlayerPlatform = (platform: string): boolean =>
  platform === Platform.Acast || platform === 'Podcast Players';

const hasSocialPlatformsInPlays = (plays: LocationPlaysResponse['plays']): boolean =>
  plays.some(({ platform }) => isSocialPlatform(platform));

const getPlatformAudienceLabel = (platform: string): string =>
  isPodcastPlayerPlatform(platform) ? 'Podcast Players' : platform;

const getPlatformPercentageLabel = (platform: string): string =>
  isPodcastPlayerPlatform(platform)
    ? '% of total podcast audience'
    : `% of total ${platform} audience`;

const formatPercentage = (value: string | number | null): string =>
  value != null ? `${value}%` : '—';

const formatPlatformValue = (platform: string, value: string | number | null): ReactNode => {
  if (value === null && isSocialPlatform(platform)) {
    return 'N/A';
  }

  return value ?? '—';
};

const buildLocationDetailsTableData = (
  plays: LocationPlaysResponse['plays'],
  countryOrder: string[],
  totalCountryDistribution: LocationData[]
): LocationDetailsRow[] => {
  const countryMap: Record<string, LocationDetailsRow> = {};

  totalCountryDistribution.forEach(({ label, percentage }) => {
    countryMap[label] = {
      key: label,
      country: getCountryName(label),
      [TOTAL_PERCENTAGE_KEY]: percentage,
    };
  });

  plays.forEach(({ platform, country_distribution }) => {
    country_distribution.forEach(({ label, value, percentage }) => {
      if (!countryMap[label]) {
        countryMap[label] = {
          key: label,
          country: getCountryName(label),
        };
      }

      countryMap[label][`${platform}Value`] = value;
      countryMap[label][`${platform}Percentage`] = percentage;
    });
  });

  return countryOrder
    .map((countryLabel) => countryMap[countryLabel])
    .filter((row): row is LocationDetailsRow => row != null);
};

const buildLocationDetailsTableColumns = (
  plays: LocationPlaysResponse['plays'],
  hideSocials: boolean,
  hasTotalCountryDistribution: boolean,
  hasSocialPlatforms: boolean
): LocationDetailsColumn[] => [
  {
    key: 'flag',
    header: '',
    className: 'w-10',
    render: (row) =>
      hasCountryFlag(row.key) ? (
        <span aria-label={`${row.country} flag`} role="img">
          {getCountryFlagEmoji(row.key)}
        </span>
      ) : null,
  },
  {
    key: 'country',
    header: 'Country',
    csvHeader: 'Country',
    render: (row) => row.country,
  },
  ...(!hideSocials && hasTotalCountryDistribution && hasSocialPlatforms
    ? [
        {
          key: TOTAL_PERCENTAGE_KEY,
          header: 'Total %',
          render: (row: LocationDetailsRow) => formatPercentage(row[TOTAL_PERCENTAGE_KEY]),
          align: 'right' as const,
          className: 'px-2 w-24 max-w-24',
          sortable: true,
          type: 'percentage' as const,
        },
      ]
    : []),
  ...plays.filter(({ platform }) => !hideSocials || !isSocialPlatform(platform)).flatMap((play) => {
    const { platform } = play;
    const valueColumn = {
      key: `${platform}Value`,
      header: getPlatformAudienceLabel(platform),
      render: (row: LocationDetailsRow) => formatPlatformValue(platform, row[`${platform}Value`]),
      align: 'right' as const,
      className: 'px-2',
      sortable: true,
      type: 'number' as const,
    };
    const percentageColumn = {
      key: `${platform}Percentage`,
      header: getPlatformPercentageLabel(platform),
      render: (row: LocationDetailsRow) => formatPercentage(row[`${platform}Percentage`]),
      align: 'right' as const,
      className: 'px-2 w-32 max-w-32',
      sortable: true,
      type: 'percentage' as const,
    };

    return [valueColumn, percentageColumn];
  }),
];

const buildLocationDetailsHeaderData = (
  columns: LocationDetailsColumn[]
): TableHeader[] =>
  columns.map((column) => ({
    id: column.key,
    label: column.header,
    sortable: column.sortable ?? false,
    className: column.className,
    styleOptions: {
      align: column.align,
    },
  }));

const buildLocationDetailsDataTableRows = (
  tableData: LocationDetailsRow[],
  columns: LocationDetailsColumn[]
): TableData[] =>
  tableData.map((row) => ({
    id: row.key,
    columns: columns.map((column) => ({
      id: column.key,
      value: column.render(row),
      type: column.type,
    })),
  }));

const getDefaultLocationDetailsSortKey = (columns: LocationDetailsColumn[]): string | undefined =>
  columns.find((column) => column.sortable)?.key;

const buildPercentageMapData = (countryDistribution: LocationData[]): LocationData[] =>
  countryDistribution.map(({ label, percentage }) => ({
    label,
    value: percentage,
    percentage,
  }));

const buildPodcastPlayerMapData = (plays: LocationPlaysResponse['plays']): LocationData[] =>
  buildPercentageMapData(
    plays.find(({ platform }) => isPodcastPlayerPlatform(platform))?.country_distribution ?? []
  );

const formatMapPercentage = (value: number): string => `${value.toFixed(2)}%`;

const LocationMapLegend = () => (
  <ul
    aria-label="Map colour scale"
    className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-textMedGrey"
  >
    {mapLegendItems.map(({ label, color }) => (
      <li key={label} className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className="inline-block size-3 rounded-sm"
          style={{ backgroundColor: color }}
        />
        <span>{label}</span>
      </li>
    ))}
  </ul>
);

const LocationModalContent = ({
  dateRange,
  selectedShow,
  hideSocials,
}: {
  dateRange: DateRange;
  selectedShow: ShowFilter;
  hideSocials: boolean;
}) => {
  const { selectedNetwork } = useDerivedNetworkContext();
  const { showAudienceSocialViews } = useFlags<{ showAudienceSocialViews?: boolean }>();
  const showSocialViews = showAudienceSocialViews === true;
  const showIds = selectedShow !== 'all' && selectedShow ? [selectedShow] : undefined;

  const { data, isPending } = useLocationPlaysQuery(
    selectedNetwork.id,
    dateRange.from,
    dateRange.to,
    undefined,
    { showIds, excludeSocials: !showSocialViews }
  );

  const plays = useMemo(() => data?.plays ?? [], [data?.plays]);
  const countryOrder = useMemo(() => data?.country_order ?? [], [data?.country_order]);
  const totalCountryDistribution = useMemo(
    () => data?.total_country_distribution ?? [],
    [data?.total_country_distribution]
  );
  const tableData = useMemo(
    () => buildLocationDetailsTableData(plays, countryOrder, totalCountryDistribution),
    [countryOrder, plays, totalCountryDistribution]
  );
  const hasSocialPlatforms = useMemo(() => hasSocialPlatformsInPlays(plays), [plays]);
  const [countrySearch, setCountrySearch] = useState('');
  const columns = useMemo(
    () =>
      buildLocationDetailsTableColumns(
        plays,
        hideSocials,
        totalCountryDistribution.length > 0,
        hasSocialPlatforms
      ),
    [hasSocialPlatforms, hideSocials, plays, totalCountryDistribution.length]
  );
  const defaultSortKey = useMemo(() => getDefaultLocationDetailsSortKey(columns), [columns]);
  const headerData = useMemo(() => buildLocationDetailsHeaderData(columns), [columns]);
  const filteredTableData = useMemo(() => {
    const normalizedSearch = countrySearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return tableData;
    }

    return tableData.filter((row) => row.country.toLowerCase().includes(normalizedSearch));
  }, [countrySearch, tableData]);
  const dataTableRows = useMemo(
    () => buildLocationDetailsDataTableRows(filteredTableData, columns),
    [columns, filteredTableData]
  );
  const mapData = useMemo(
    () =>
      hideSocials
        ? buildPodcastPlayerMapData(plays)
        : buildPercentageMapData(totalCountryDistribution),
    [hideSocials, plays, totalCountryDistribution]
  );

  if (isPending) {
    return (
      <section className="px-6 py-10 flex flex-col justify-center items-center h-[60vh] gap-10">
        <Loader data-testid="loader" />
      </section>
    );
  }

  if (!data || data.plays.length < 1) {
    return <NoDataMessage dataType="location data" />;
  }

  return (
    <section className="px-6 py-10 flex flex-col gap-10">
      <div className="flex justify-center">
        <div className="w-full max-w-3xl">
          <LocationMap
            platformData={mapData}
            valueFormatter={formatMapPercentage}
            valueScale="percentage"
          />
          <LocationMapLegend />
        </div>
      </div>
      <div className="flex w-full items-center gap-3">
        <label className="relative block min-w-0 flex-1">
          <span className="sr-only">Search country</span>
          <SearchLightGlyph
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 fill-textMedGrey"
          />
          <input
            className="h-10 w-full rounded border border-borderMedGrey py-2 pl-9 pr-3 text-sm text-textDarkGrey"
            placeholder="Search country"
            type="search"
            value={countrySearch}
            onChange={(event) => {
              setCountrySearch(event.target.value);
            }}
          />
        </label>
      </div>
      <DataTable
        key={defaultSortKey}
        headerData={headerData}
        tableData={dataTableRows}
        initialState={
          defaultSortKey ? { sorting: { key: defaultSortKey, direction: 'desc' } } : undefined
        }
        pagination={{
          summary: (from, to, total) => (
            <span>
              Showing {from}-{to} of {total}
            </span>
          ),
          defaultPageSize: DEFAULT_PAGE_SIZE,
          pageSizeOptions: [10, 25, 50],
        }}
        renderRow={(row) => (
          <tr
            key={row.id}
            className="bg-white border-b border-borderMedGrey"
          >
            {row.columns.map((column, columnIndex) => (
              <td
                key={column.id}
                className={twMerge(
                  'py-4 text-sm text-textDarkGrey',
                  columns[columnIndex]?.align === 'right' ? 'text-right' : 'text-left'
                )}
              >
                {column.value}
              </td>
            ))}
          </tr>
        )}
      />
    </section>
  );
};

const LocationModalActions = ({
  dateRange,
  selectedShow,
  hideSocials,
  onHideSocialsChange,
}: {
  dateRange: DateRange;
  selectedShow: ShowFilter;
  hideSocials: boolean;
  onHideSocialsChange: (hideSocials: boolean) => void;
}) => {
  const { selectedNetwork } = useDerivedNetworkContext();
  const { showAudienceSocialViews } = useFlags<{ showAudienceSocialViews?: boolean }>();
  const showSocialViews = showAudienceSocialViews === true;
  const showIds = selectedShow !== 'all' && selectedShow ? [selectedShow] : undefined;
  const { data, isPending } = useLocationPlaysQuery(
    selectedNetwork.id,
    dateRange.from,
    dateRange.to,
    undefined,
    { showIds, excludeSocials: !showSocialViews }
  );

  if (isPending || !data || !hasSocialPlatformsInPlays(data.plays)) {
    return null;
  }

  return (
    <div className="shrink-0 whitespace-nowrap">
      <Switch
        id="toggle-hide-location-socials"
        label="Hide socials"
        checked={hideSocials}
        onChange={() => {
          const nextHideSocials = !hideSocials;
          onHideSocialsChange(nextHideSocials);
          trackAudienceLocationModalHideSocialsToggled(nextHideSocials);
        }}
      />
    </div>
  );
};

const LocationModal = () => {
  const { handleCSVExport } = useLocationCsvExport();
  const [hideSocials, setHideSocials] = useState(false);

  return (
    <Modal
      hashProp="location-details"
      title="Location details"
      showDateRangeFilter
      onOpen={trackAudienceLocationModalOpened}
      downloadConfig={{
        onDownload: (...args) => {
          trackAudienceLocationModalDownloadClicked();
          return handleCSVExport(...args);
        },
        downloadButtonText: 'Export',
        downloadButtonIcon: ArrowDownToLineLightGlyph,
      }}
      actions={(dateRange, selectedShow) => (
        <LocationModalActions
          dateRange={dateRange}
          selectedShow={selectedShow}
          hideSocials={hideSocials}
          onHideSocialsChange={setHideSocials}
        />
      )}
      renderTrigger={(openModal) => (
        <div className="flex flex-row justify-end">
          <Button
            className="self-right"
            variant="tertiary"
            glyphEnd={ChevronRightRegularGlyph}
            onClick={openModal}
          >
            All countries
          </Button>
        </div>
      )}
    >
      {(dateRange, selectedShow) => (
        <LocationModalContent
          dateRange={dateRange}
          selectedShow={selectedShow}
          hideSocials={hideSocials}
        />
      )}
    </Modal>
  );
};

export default LocationModal;

